import React, { useState, useEffect } from 'react';
import { 
  KNOWN_SUPABASE_ICONS, 
  getIconUrl, 
  testIconAvailability, 
  getAvailableIcons, 
  IconFile,
  SEMANTIC_ICON_MAP 
} from '@/utils/SupabaseIconWorkaround';

interface SupabaseIconGalleryProps {
  showOnlyAvailable?: boolean;
  showSemanticMap?: boolean;
  categoryFilter?: IconFile['category'] | 'all';
}

export const SupabaseIconGallery: React.FC<SupabaseIconGalleryProps> = ({
  showOnlyAvailable = true,
  showSemanticMap = false,
  categoryFilter = 'all'
}) => {
  const [availableIcons, setAvailableIcons] = useState<IconFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadIcons = async () => {
      setLoading(true);
      
      if (showOnlyAvailable) {
        const available = await getAvailableIcons();
        setAvailableIcons(available);
      } else {
        setAvailableIcons(KNOWN_SUPABASE_ICONS);
      }
      
      // Test each icon individually for visual feedback
      const results: Record<string, boolean> = {};
      for (const icon of KNOWN_SUPABASE_ICONS) {
        results[icon.name] = await testIconAvailability(icon.name);
      }
      setTestResults(results);
      
      setLoading(false);
    };

    loadIcons();
  }, [showOnlyAvailable]);

  const filteredIcons = categoryFilter === 'all' 
    ? availableIcons 
    : availableIcons.filter(icon => icon.category === categoryFilter);

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
    console.log(`Copied ${type}:`, text);
  };

  if (loading) {
    return (
      <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="text-blue-800 font-medium">Testing icon availability...</p>
        </div>
      </div>
    );
  }

  const availableCount = Object.values(testResults).filter(Boolean).length;
  const totalCount = KNOWN_SUPABASE_ICONS.length;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <h3 className="text-green-800 font-semibold mb-2">
          ✅ Icon Availability Status
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-green-600 font-medium">Available:</span>
            <span className="ml-1 text-green-800">{availableCount}</span>
          </div>
          <div>
            <span className="text-green-600 font-medium">Total Known:</span>
            <span className="ml-1 text-green-800">{totalCount}</span>
          </div>
          <div>
            <span className="text-green-600 font-medium">Success Rate:</span>
            <span className="ml-1 text-green-800">
              {((availableCount / totalCount) * 100).toFixed(1)}%
            </span>
          </div>
          <div>
            <span className="text-green-600 font-medium">Showing:</span>
            <span className="ml-1 text-green-800">{filteredIcons.length}</span>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {['all', 'avatar', 'badge', 'ui', 'state', 'functional'].map(category => (
          <button
            key={category}
            onClick={() => window.location.href = `?category=${category}`}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              categoryFilter === category 
                ? 'bg-blue-100 text-blue-800 border-blue-300' 
                : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Icons Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {filteredIcons.map((icon) => {
          const isAvailable = testResults[icon.name];
          const iconUrl = getIconUrl(icon.name);
          
          return (
            <div
              key={icon.name}
              className={`p-4 rounded-lg border transition-colors ${
                isAvailable 
                  ? 'bg-white border-green-200 hover:border-green-300' 
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="aspect-square mb-3 flex items-center justify-center">
                {isAvailable ? (
                  <img
                    src={iconUrl}
                    alt={icon.description}
                    className="w-20 h-20 object-contain rounded-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400 text-sm">404</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-800 truncate" title={icon.name}>
                  {icon.name}
                </p>
                <p className="text-xs text-gray-600 leading-tight">
                  {icon.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    icon.category === 'avatar' ? 'bg-purple-100 text-purple-700' :
                    icon.category === 'badge' ? 'bg-yellow-100 text-yellow-700' :
                    icon.category === 'ui' ? 'bg-blue-100 text-blue-700' :
                    icon.category === 'state' ? 'bg-red-100 text-red-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {icon.category}
                  </span>
                  <span className={`text-xs ${isAvailable ? 'text-green-600' : 'text-red-500'}`}>
                    {isAvailable ? '✅' : '❌'}
                  </span>
                </div>
              </div>
              
              {isAvailable && (
                <div className="mt-3 space-y-1">
                  <button
                    onClick={() => copyToClipboard(`<SupabaseIcon iconPath="${icon.name}" alt="${icon.description}" />`, 'Component')}
                    className="w-full text-xs bg-blue-50 text-blue-600 py-1 px-2 rounded hover:bg-blue-100 transition-colors"
                  >
                    Copy Component
                  </button>
                  <button
                    onClick={() => copyToClipboard(iconUrl, 'URL')}
                    className="w-full text-xs bg-gray-50 text-gray-600 py-1 px-2 rounded hover:bg-gray-100 transition-colors"
                  >
                    Copy URL
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Semantic Map */}
      {showSemanticMap && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-blue-800 font-semibold mb-3">🏷️ Semantic Icon Map</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {Object.entries(SEMANTIC_ICON_MAP).map(([semantic, filename]) => {
              const iconUrl = getIconUrl(filename);
              const isAvailable = testResults[filename];
              return (
                <div key={semantic} className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                  <div className="w-8 h-8 flex items-center justify-center">
                    {isAvailable ? (
                      <img
                        src={iconUrl}
                        alt={semantic}
                        className="w-6 h-6 object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-gray-400 text-xs">?</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <code className="text-blue-600 font-mono text-xs font-semibold">{semantic}</code>
                    <div className="text-gray-500 text-xs truncate">{filename}</div>
                  </div>
                  <span className={`text-xs ${isAvailable ? 'text-green-600' : 'text-red-500'}`}>
                    {isAvailable ? '✅' : '❌'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Usage Examples */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-gray-800 font-semibold mb-3">💡 Usage Examples</h3>
        <div className="space-y-2 text-sm font-mono">
          <div className="p-2 bg-white rounded border">
            <code className="text-purple-600">
              {`<SupabaseIcon iconPath="lyra-avatar.png" alt="Lyra Avatar" size="lg" />`}
            </code>
          </div>
          <div className="p-2 bg-white rounded border">
            <code className="text-purple-600">
              {`const url = getIconUrl("achievement-trophy.png");`}
            </code>
          </div>
          <div className="p-2 bg-white rounded border">
            <code className="text-purple-600">
              {`const lyraIcon = getSemanticIcon("mentor");`}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupabaseIconGallery;